const DEFAULT_SLOW_RESPONSE_MS = 12000;
const DEFAULT_STALLED_RESPONSE_MS = 30000;

function elapsedMs(startedAt, now) {
    return Math.max(0, Math.round(now() - startedAt));
}

export function createVideocallDiagnostics(options) {
    const now = options.now || (() => Date.now());
    const setTimer = options.setTimer || setTimeout;
    const clearTimer = options.clearTimer || clearTimeout;
    const slowResponseMs = options.slowResponseMs || DEFAULT_SLOW_RESPONSE_MS;
    const stalledResponseMs = options.stalledResponseMs || DEFAULT_STALLED_RESPONSE_MS;
    const traceId = `${now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
    let turnCounter = 0;
    let activeTurn = null;
    let slowTimer = null;
    let stalledTimer = null;

    function state() {
        try { return options.getState ? options.getState() : {}; }
        catch (error) { return { stateSnapshotError: error && error.message }; }
    }

    function log(event, details = {}) {
        options.debugLog(`[trace:${traceId}] ${event}`, Object.assign({}, details, {
            turn: activeTurn ? activeTurn.id : null,
            state: state(),
        }));
    }

    function clearResponseTimers() {
        if (slowTimer) clearTimer(slowTimer);
        if (stalledTimer) clearTimer(stalledTimer);
        slowTimer = null;
        stalledTimer = null;
    }

    function beginTurn(data, metadata = {}) {
        clearResponseTimers();
        turnCounter += 1;
        activeTurn = {
            id: turnCounter,
            submittedAt: now(),
            acknowledgedAt: null,
            responseStartedAt: null,
        };
        log('turn submitted', {
            type: data.type,
            payloadBytes: metadata.payloadBytes || 0,
            audioBytes: data.type === 'audio' && data.message ? data.message.length : 0,
            transcriptionLength: data.transcription ? data.transcription.length : 0,
        });

        slowTimer = setTimer(() => {
            if (!activeTurn || activeTurn.responseStartedAt) return;
            log('response is slower than expected', {
                elapsedMs: elapsedMs(activeTurn.submittedAt, now),
                acknowledged: !!activeTurn.acknowledgedAt,
            });
            if (options.onSlowResponse) options.onSlowResponse(activeTurn);
        }, slowResponseMs);

        stalledTimer = setTimer(() => {
            if (!activeTurn || activeTurn.responseStartedAt) return;
            log('response appears stalled', {
                elapsedMs: elapsedMs(activeTurn.submittedAt, now),
                acknowledged: !!activeTurn.acknowledgedAt,
            });
            if (options.onStalledResponse) options.onStalledResponse(activeTurn);
        }, stalledResponseMs);
    }

    function onMessageSent(data, metadata = {}) {
        if (data.type === 'audio' || data.type === 'text') {
            beginTurn(data, metadata);
            return;
        }
        log('client message sent', { type: data.type, payloadBytes: metadata.payloadBytes || 0 });
    }

    function onSendFailed(data, reason) {
        log('client message send failed', { type: data.type, reason: reason || 'unknown' });
        if (activeTurn && (data.type === 'audio' || data.type === 'text')) clearResponseTimers();
    }

    function onMessageReceived(data, metadata = {}) {
        const type = data.type || 'unknown';
        if (!activeTurn) {
            log('server message received', { type: type, payloadBytes: metadata.payloadBytes || 0 });
            return;
        }

        const turnAge = elapsedMs(activeTurn.submittedAt, now);
        if (!activeTurn.acknowledgedAt && ['speech_started', 'speech_stopped', 'user_transcription'].includes(type)) {
            activeTurn.acknowledgedAt = now();
            log('turn acknowledged by server', { type: type, latencyMs: turnAge });
            return;
        }

        if (!activeTurn.responseStartedAt && ['audio_response', 'audio', 'audio_chunk', 'text', 'text_chunk'].includes(type)) {
            activeTurn.responseStartedAt = now();
            clearResponseTimers();
            log('assistant response started', { type: type, latencyMs: turnAge });
            if (options.onResponseStarted) options.onResponseStarted(activeTurn);
            return;
        }

        if (type === 'response_complete' || type === 'response_completed') {
            clearResponseTimers();
            log('assistant response completed', { type: type, elapsedMs: turnAge });
            if (type === 'response_completed') activeTurn = null;
            return;
        }

        if (type === 'error' || data.error) {
            clearResponseTimers();
            log('server returned an error for active turn', { type: type, elapsedMs: turnAge });
        }
    }

    function reset(reason) {
        clearResponseTimers();
        log('diagnostics reset', { reason: reason || 'unspecified' });
        activeTurn = null;
    }

    return {
        traceId: traceId,
        log: log,
        onMessageSent: onMessageSent,
        onSendFailed: onSendFailed,
        onMessageReceived: onMessageReceived,
        reset: reset,
    };
}
